import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const VisualIdentityStep = ({ formData, updateFormData, onNext, onPrevious }) => {
  const [errors, setErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [galleryPreviews, setGalleryPreviews] = useState([]);

  const handleLogoUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file?.size > 2 * 1024 * 1024) {
        setErrors({ ...errors, logo: 'El logo no debe superar 2MB' });
        return;
      }

      if (!['image/jpeg', 'image/png', 'image/jpg']?.includes(file?.type)) {
        setErrors({ ...errors, logo: 'Solo se permiten archivos JPG o PNG' });
        return;
      }

      updateFormData({ logo: file });
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader?.result);
      };
      reader?.readAsDataURL(file);

      setErrors({ ...errors, logo: null });
    }
  };

  const handleCoverUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file?.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, coverImage: 'La imagen de portada no debe superar 5MB' });
        return;
      }

      if (!['image/jpeg', 'image/png', 'image/jpg']?.includes(file?.type)) {
        setErrors({ ...errors, coverImage: 'Solo se permiten archivos JPG o PNG' });
        return;
      }

      updateFormData({ coverImage: file });
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader?.result);
      };
      reader?.readAsDataURL(file);

      setErrors({ ...errors, coverImage: null });
    }
  };

  const handleGalleryUpload = (e) => {
    const files = Array.from(e?.target?.files || []);
    const currentImages = formData?.galleryImages || [];
    
    if (currentImages?.length + files?.length > 5) {
      setErrors({ ...errors, galleryImages: 'Máximo 5 imágenes permitidas' });
      return;
    }

    const validFiles = files?.filter(file => {
      if (file?.size > 5 * 1024 * 1024) return false;
      if (!['image/jpeg', 'image/png', 'image/jpg']?.includes(file?.type)) return false;
      return true;
    });

    if (validFiles?.length !== files?.length) {
      setErrors({ ...errors, galleryImages: 'Algunas imágenes fueron rechazadas (máx 5MB, JPG/PNG)' });
    }

    updateFormData({ galleryImages: [...currentImages, ...validFiles] });

    validFiles?.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setGalleryPreviews(prev => [...prev, reader?.result]);
      };
      reader?.readAsDataURL(file);
    });

    setErrors({ ...errors, galleryImages: null });
  };

  const removeGalleryImage = (index) => {
    const newImages = formData?.galleryImages?.filter((_, i) => i !== index);
    const newPreviews = galleryPreviews?.filter((_, i) => i !== index);
    
    updateFormData({ galleryImages: newImages });
    setGalleryPreviews(newPreviews);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.logo) {
      newErrors.logo = 'El logo es requerido';
    }

    if (!formData?.coverImage) {
      newErrors.coverImage = 'La imagen de portada es requerida';
    }

    if (!formData?.galleryImages || formData?.galleryImages?.length === 0) {
      newErrors.galleryImages = 'Debes subir al menos 1 imagen de productos/servicios';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleContinue = () => {
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-foreground mb-2">Identidad Visual</h2>
        <p className="text-muted-foreground">Sube imágenes que representen tu negocio y atraigan clientes</p>
      </div>

      {/* Logo Upload */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-foreground">
          Logo del Negocio <span className="text-destructive">*</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border-3 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-accent transition-colors cursor-pointer relative">
            <input
              type="file"
              accept="image/jpeg,image/png,image/jpg"
              onChange={handleLogoUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            
            {logoPreview ? (
              <div className="space-y-3">
                <img
                  src={logoPreview}
                  alt="Vista previa del logo"
                  className="w-32 h-32 mx-auto object-contain rounded-lg"
                />
                <div className="flex items-center justify-center gap-2 text-green-600">
                  <Icon name="CheckCircle" size={20} />
                  <span className="font-semibold">Logo cargado</span>
                </div>
                <p className="text-sm text-muted-foreground">Haz clic para cambiar</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-center">
                  <div className="bg-accent/10 rounded-full p-4">
                    <Icon name="Image" size={32} className="text-accent" />
                  </div>
                </div>
                <div>
                  <p className="font-semibold text-foreground">Sube tu logo</p>
                  <p className="text-sm text-muted-foreground mt-1">JPG o PNG (máx. 2MB)</p>
                </div>
              </div>
            )}
          </div>

          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-900">
                <p className="font-semibold mb-2">Recomendaciones para el logo:</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Formato cuadrado (1:1) preferible</li>
                  <li>Fondo transparente o blanco</li>
                  <li>Resolución mínima: 500x500px</li>
                  <li>Logo legible y de alta calidad</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        {errors?.logo && (
          <p className="text-sm text-destructive flex items-center gap-1">
            <Icon name="AlertCircle" size={14} />
            {errors?.logo}
          </p>
        )}
      </div>

      {/* Cover Image Upload */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-foreground">
          Imagen de Portada <span className="text-destructive">*</span>
        </label>
        <div className="border-3 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-accent transition-colors cursor-pointer relative h-48">
          <input
            type="file"
            accept="image/jpeg,image/png,image/jpg"
            onChange={handleCoverUpload}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          
          {coverPreview ? (
            <div className="h-full flex flex-col items-center justify-center space-y-3">
              <img
                src={coverPreview}
                alt="Vista previa de portada"
                className="max-h-32 mx-auto rounded-lg shadow-md object-cover"
              />
              <div className="flex items-center justify-center gap-2 text-green-600">
                <Icon name="CheckCircle" size={20} />
                <span className="font-semibold">Portada cargada</span>
              </div>
              <p className="text-sm text-muted-foreground">Haz clic para cambiar</p>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center space-y-3">
              <div className="flex justify-center">
                <div className="bg-accent/10 rounded-full p-4">
                  <Icon name="ImagePlus" size={32} className="text-accent" />
                </div>
              </div>
              <div>
                <p className="font-semibold text-foreground">Sube imagen de portada</p>
                <p className="text-sm text-muted-foreground mt-1">JPG o PNG (máx. 5MB) • 1920x1080px recomendado</p>
              </div>
            </div>
          )}
        </div>
        {errors?.coverImage && (
          <p className="text-sm text-destructive flex items-center gap-1">
            <Icon name="AlertCircle" size={14} />
            {errors?.coverImage}
          </p>
        )}
      </div>

      {/* Gallery Upload */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-foreground">
          Galería de Productos/Servicios <span className="text-destructive">*</span>
          <span className="text-muted-foreground font-normal ml-2">(Mínimo 1, Máximo 5 imágenes)</span>
        </label>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {galleryPreviews?.map((preview, index) => (
            <div key={index} className="relative group">
              <img
                src={preview}
                alt={`Galería ${index + 1}`}
                className="w-full h-32 object-cover rounded-lg shadow-md"
              />
              <button
                type="button"
                onClick={() => removeGalleryImage(index)}
                className="absolute top-2 right-2 bg-destructive text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Icon name="X" size={16} />
              </button>
              <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                {index + 1} de 5
              </div>
            </div>
          ))}

          {(formData?.galleryImages?.length || 0) < 5 && (
            <div className="border-3 border-dashed border-gray-300 rounded-xl h-32 flex items-center justify-center hover:border-accent transition-colors cursor-pointer relative">
              <input
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handleGalleryUpload}
                multiple
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="text-center">
                <div className="flex justify-center mb-2">
                  <div className="bg-accent/10 rounded-full p-2">
                    <Icon name="Plus" size={24} className="text-accent" />
                  </div>
                </div>
                <p className="text-xs font-semibold text-muted-foreground">
                  Agregar<br/>imágenes
                </p>
              </div>
            </div>
          )}
        </div>

        {errors?.galleryImages && (
          <p className="text-sm text-destructive flex items-center gap-1">
            <Icon name="AlertCircle" size={14} />
            {errors?.galleryImages}
          </p>
        )}
      </div>

      <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-yellow-900">
            <p className="font-semibold mb-2">Tips para mejores resultados:</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Usa imágenes de alta calidad y bien iluminadas</li>
              <li>Muestra tus productos o servicios más populares</li>
              <li>Evita imágenes borrosas o con marcas de agua</li>
              <li>Las imágenes atractivas aumentan las visitas en un 80%</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex gap-4 pt-6 border-t">
        <Button
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconPosition="left"
          className="flex-1"
        >
          Regresar
        </Button>

        <Button
          variant="default"
          size="lg"
          onClick={handleContinue}
          iconName="ArrowRight"
          iconPosition="right"
          className="flex-1 bg-gradient-to-r from-accent to-orange-600 hover:shadow-lg transform hover:scale-105 transition-all"
        >
          Continuar
        </Button>
      </div>
    </div>
  );
};

export default VisualIdentityStep;